import { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseAsString, useQueryState } from 'nuqs';
import { AuthContext } from '@@/contexts/AuthContext';
import { Badge } from '@@/ui/primitives';
import { PageHeader } from '@@/ui/components';
import { DataTable, type ColumnDef } from '../components/shared/data-table';
import { FilterPanel, type FilterSection } from '../components/shared/filter-panel';
import { CampaignType, type CampaignStatus, type DashboardCampaign } from '../types';

const mockCampaigns: DashboardCampaign[] = [
  {
    id: '1',
    name: 'Community Feedback Survey',
    agencyId: 'a1',
    agencyName: 'Agency Name Placeholder A',
    type: CampaignType.SURVEY_VOICE,
    contacts: 1200,
    deliveryRate: 68.1,
    status: 'active'
  },
  {
    id: '2',
    name: 'Community Meeting Invitation',
    agencyId: 'a2',
    agencyName: 'Name Placeholder E',
    type: CampaignType.SURVEY_VOICE,
    contacts: 1200,
    deliveryRate: 68.1,
    status: 'active'
  },
  {
    id: '3',
    name: 'Emergency Alert - Weather',
    agencyId: 'a3',
    agencyName: 'Agency Name Placeholder C',
    type: CampaignType.NOTIFICATION_VOICE,
    contacts: 800,
    deliveryRate: 92.4,
    status: 'active'
  },
  {
    id: '4',
    name: 'Resident Satisfaction Survey',
    agencyId: 'a4',
    agencyName: 'Name Placeholder B',
    type: CampaignType.NOTIFICATION_VOICE,
    contacts: 800,
    deliveryRate: 92.4,
    status: 'active'
  },
  {
    id: '5',
    name: 'Emergency Contact Verification',
    agencyId: 'a3',
    agencyName: 'Agency Name Placeholder C',
    type: CampaignType.NOTIFICATION_SMS,
    contacts: 500,
    deliveryRate: 84.9,
    status: 'scheduled'
  },
  {
    id: '6',
    name: 'Utility Maintenance Notice',
    agencyId: 'a1',
    agencyName: 'Agency Name Placeholder A',
    type: CampaignType.NOTIFICATION_SMS,
    contacts: 500,
    deliveryRate: 84.9,
    status: 'scheduled'
  },
  {
    id: '7',
    name: 'Service Update SMS',
    agencyId: 'a5',
    agencyName: 'Agency Name Placeholder D',
    type: CampaignType.SURVEY_SMS,
    contacts: 1000,
    deliveryRate: 91.2,
    status: 'completed'
  },
  {
    id: '8',
    name: 'Billing Notification',
    agencyId: 'a4',
    agencyName: 'Agency Name Placeholder B',
    type: CampaignType.SURVEY_SMS,
    contacts: 1000,
    deliveryRate: 91.2,
    status: 'completed'
  },
  {
    id: '9',
    name: 'Health & Wellness Tips',
    agencyId: 'a6',
    agencyName: 'Name Placeholder J',
    type: CampaignType.NOTIFICATION_VOICE,
    contacts: 800,
    deliveryRate: 88.7,
    status: 'completed'
  }
];

const TOTAL_CAMPAIGNS = 1469;

const campaignTypeLabels: Record<CampaignType, string> = {
  [CampaignType.SURVEY_VOICE]: 'Voice survey',
  [CampaignType.SURVEY_SMS]: 'SMS survey',
  [CampaignType.NOTIFICATION_VOICE]: 'Voice notification',
  [CampaignType.NOTIFICATION_SMS]: 'SMS notification'
};

const statusStyles: Record<CampaignStatus, string> = {
  active: 'bg-blue text-white',
  scheduled: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-50 text-green-800'
};

const uniqueAgencies = [...new Set(mockCampaigns.map((c) => c.agencyName))];

const filterSections: FilterSection[] = [
  {
    key: 'agency',
    label: 'Agency Name',
    options: uniqueAgencies.map((name) => ({ value: name, label: name }))
  },
  {
    key: 'type',
    label: 'Type',
    options: [
      { value: CampaignType.SURVEY_VOICE, label: 'Voice Survey' },
      { value: CampaignType.SURVEY_SMS, label: 'SMS Survey' },
      { value: CampaignType.NOTIFICATION_VOICE, label: 'Voice Notification' },
      { value: CampaignType.NOTIFICATION_SMS, label: 'SMS Notification' }
    ]
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'completed', label: 'Completed' }
    ]
  }
];

const DashboardPageContent = () => {
  const { getPathWithInstanceAlias } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useQueryState('search', parseAsString.withDefault(''));
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({
    agency: new Set(),
    type: new Set(),
    status: new Set()
  });

  const handleFilterChange = useCallback((sectionKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const newSet = new Set(prev[sectionKey]);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return { ...prev, [sectionKey]: newSet };
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedFilters({
      agency: new Set(),
      type: new Set(),
      status: new Set()
    });
  }, [setSearchTerm]);

  const handleApplyFilters = useCallback(() => {
    // Filters are applied automatically via filteredCampaigns
  }, []);

  const handleCreateCampaign = useCallback(() => {
    // Create campaign logic would go here
  }, []);

  const handleLoadMore = useCallback(() => {
    // Load more logic would go here
  }, []);

  const filteredCampaigns = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const { agency, type, status } = selectedFilters;

    return mockCampaigns.filter((c) => {
      const matchesSearch = !term || c.name.toLowerCase().includes(term) || c.agencyName.toLowerCase().includes(term);
      const matchesAgency = agency.size === 0 || agency.has(c.agencyName);
      const matchesType = type.size === 0 || type.has(c.type);
      const matchesStatus = status.size === 0 || status.has(c.status);

      return matchesSearch && matchesAgency && matchesType && matchesStatus;
    });
  }, [searchTerm, selectedFilters]);

  const campaignColumns: ColumnDef<DashboardCampaign>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Campaign Name',
        headerClassName: 'text-sm font-semibold text-foreground',
        render: (campaign) => (
          <Link
            to={getPathWithInstanceAlias(`/portal/campaigns/${campaign.id}`)}
            className='text-sm text-blue underline'
          >
            {campaign.name}
          </Link>
        )
      },
      {
        key: 'agency',
        header: 'Agency',
        headerClassName: 'text-sm font-semibold text-foreground',
        render: (campaign) => (
          <Link
            to={getPathWithInstanceAlias(`/portal/campaigns/agency/${campaign.agencyId}`)}
            className='text-sm text-blue underline'
          >
            {campaign.agencyName}
          </Link>
        )
      },
      {
        key: 'type',
        header: 'Type',
        headerClassName: 'text-sm font-semibold text-foreground',
        render: (campaign) => (
          <Badge className='bg-gray-400 text-foreground border-0'>{campaignTypeLabels[campaign.type]}</Badge>
        )
      },
      {
        key: 'contacts',
        header: 'Contacts',
        headerClassName: 'text-sm font-semibold text-foreground',
        cellClassName: 'text-sm tabular-nums',
        render: (campaign) => campaign.contacts.toLocaleString()
      },
      {
        key: 'deliveryRate',
        header: 'Delivery Rate',
        headerClassName: 'text-sm font-semibold text-foreground',
        cellClassName: 'text-sm tabular-nums',
        render: (campaign) => `${campaign.deliveryRate}%`
      },
      {
        key: 'status',
        header: 'Status',
        headerClassName: 'text-sm text-foreground',
        render: (campaign) => (
          <Badge className={`font-normal ${statusStyles[campaign.status]}`}>{campaign.status}</Badge>
        )
      }
    ],
    [getPathWithInstanceAlias]
  );

  return (
    <div className='flex flex-col'>
      <PageHeader title='Campaigns Dashboard' description='Track your agencies and campaigns performances' />

      <div className='w-full px-10 py-6 flex-1 space-y-4 overflow-y-auto'>
        <div className='flex gap-6'>
          <FilterPanel
            sections={filterSections}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
          />
          <DataTable
            title='Campaigns'
            data={filteredCampaigns}
            totalCount={TOTAL_CAMPAIGNS}
            columns={campaignColumns}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchLabel='Search List'
            searchPlaceholder='Search by campaign or agency name'
            showCheckboxes={false}
            createButtonLabel='Create Campaign'
            onCreateClick={handleCreateCampaign}
            showLoadMore
            onLoadMore={handleLoadMore}
          />
        </div>
      </div>
    </div>
  );
};

export const DashboardPage = DashboardPageContent;
